import { z } from "zod/v4";

// Shape of the RWA (real-world asset) fundamentals served by the Decibel BFF,
// which proxies the Massive REST API. Field names are snake_case to match the
// upstream JSON, consistent with the rest of the read SDK.
//
// These are shared DTOs only — there is intentionally no `DecibelReadDex`
// reader for them (the data is fetched from the BFF, not `tradingHttpUrl`), so
// docs/typescript-sdk/read-sdk.mdx is deliberately not extended; adding a
// reader section there would imply a `readDexSdk.rwaInsights` that does not
// exist.

// Allowlist of tickers backed by RWA (real-world asset) fundamentals. Shared by
// the web app (to gate the Insights tab / after-hours stat) and the BFF (to
// reject requests for unsupported tickers before proxying the metered upstream).
// Follow-up: source this from the backend / a PerpMarket field once markets
// carry an asset-type flag, so it no longer needs hand-maintaining here.
export const RWA_TICKERS = [
  "NVDA",
  "NFLX",
  "AAPL",
  "TSLA",
  "AMZN",
  "GOOGL",
  "MSFT",
  "META",
  // Recent IPO (listed 2026-06-12). "SPCX" is a reused ticker, so the provider
  // clips its price history to the listing date to avoid the prior instrument's
  // bars corrupting volume / 52-week stats; the 52-week range stays "--" until a
  // full year of real trading exists.
  "SPCX",
  // Crypto-adjacent and fintech equities
  "MSTR",
  "COIN",
  "CRCL",
  "HOOD",
  // Semiconductors and hardware
  "AMD",
  "INTC",
  "ARM",
  "MRVL",
  "QCOM",
  "MU",
  // Enterprise tech
  "IBM",
  // International ADRs on US exchanges
  "ASML",
  "BABA",
  // Enterprise software and data
  "ORCL",
  "PLTR",
  // Hardware
  "DELL",
  // Pharma
  "LLY",
  // Ride-sharing
  "UBER",
  // Pharma
  "MRNA",
  "TTWO",
  "GME",
  "SONY",
] as const;

export type RwaTicker = (typeof RWA_TICKERS)[number];

const RWA_TICKER_SET: ReadonlySet<string> = new Set(RWA_TICKERS);

/** True if `ticker` is a supported RWA market ticker (case-sensitive, uppercase). */
export function isRwaTicker(ticker: string): ticker is RwaTicker {
  return RWA_TICKER_SET.has(ticker);
}

export const RwaDateStatusSchema = z.enum(["projected", "confirmed"]);

export const RwaEarningsQuarterSchema = z.object({
  fiscal_period: z.string(),
  fiscal_year: z.number(),
  estimated_eps: z.number().nullable(),
  actual_eps: z.number().nullable(),
  eps_surprise_pct: z.number().nullable(),
  report_date: z.string(),
  date_status: RwaDateStatusSchema,
});

export const RwaAnalystRatingsSchema = z.object({
  total_analysts: z.number(),
  strong_buy: z.number(),
  buy: z.number(),
  hold: z.number(),
  sell: z.number(),
  strong_sell: z.number(),
  consensus_price_target: z.number(),
  high_price_target: z.number(),
  low_price_target: z.number(),
});

// All fields nullable: a given account may not be entitled to every upstream
// endpoint (e.g. real-time snapshot), so the UI renders "--" for what's missing
// rather than failing the whole panel.
export const RwaKeyStatisticsSchema = z.object({
  market_cap: z.number().nullable(),
  volume: z.number().nullable(),
  average_volume: z.number().nullable(),
  pe_ratio: z.number().nullable(),
  week52_high: z.number().nullable(),
  week52_low: z.number().nullable(),
});

export const RwaInsightsSchema = z.object({
  ticker: z.string(),
  earnings: z.array(RwaEarningsQuarterSchema),
  analyst_ratings: RwaAnalystRatingsSchema,
  key_statistics: RwaKeyStatisticsSchema,
});

export const RwaSessionSchema = z.enum(["pre-market", "after-hours", "closed"]);

export const RwaAfterHoursSchema = z.object({
  ticker: z.string(),
  price: z.number(),
  previous_close: z.number(),
  change: z.number(),
  change_pct: z.number(),
  session: RwaSessionSchema,
});

export type RwaDateStatus = z.infer<typeof RwaDateStatusSchema>;
export type RwaEarningsQuarter = z.infer<typeof RwaEarningsQuarterSchema>;
export type RwaAnalystRatings = z.infer<typeof RwaAnalystRatingsSchema>;
export type RwaKeyStatistics = z.infer<typeof RwaKeyStatisticsSchema>;
export type RwaInsights = z.infer<typeof RwaInsightsSchema>;
export type RwaSession = z.infer<typeof RwaSessionSchema>;
export type RwaAfterHours = z.infer<typeof RwaAfterHoursSchema>;
